import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Crown, Medal, Compass, ShieldStar, Info } from "@phosphor-icons/react";
import api from "../api";
import { brandIconSquare } from "../brand";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = [
  { id: "frontier", label: "Frontier Score" },
  { id: "seasonal", label: "Seasonal" },
  { id: "reputation", label: "Reputation" },
  { id: "crew", label: "Crew Commanders" },
  { id: "precision", label: "Precision" },
  { id: "high_stakes", label: "High-Stakes" },
  { id: "frozen_pass", label: "Frozen Pass" },
  { id: "temple_echoes", label: "Temple of Echoes" },
  { id: "smugglers_coast", label: "Smuggler's Coast" },
  { id: "gilded_meridian", label: "Gilded Meridian" },
];

function countdown(ends) {
  if (!ends) return "";
  const ms = new Date(ends) - new Date();
  if (ms <= 0) return "Season ended";
  const d = Math.floor(ms / 86400000);
  return `${d} days remaining`;
}

export default function Leaderboards() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cat, setCat] = useState("frontier");
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get(`/leaderboards?category=${cat}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], total: 0 }));
    if (user) api.get(`/leaderboards/me?category=${cat}`).then((r) => setMe(r.data)).catch(() => setMe(null));
  }, [cat, user]);

  const rows = data?.rows || [];
  const podium = rows.slice(0, 3);
  const rest = rows.slice(3, 100);

  return (
    <div className="min-h-screen bg-ff-bg text-ff-parchment">
      <header className="border-b border-ff-brass/25 bg-ff-bg/90 px-4 py-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <button onClick={() => nav("/")} className="flex items-center gap-2" data-testid="lb-home">
            <img src={brandIconSquare} alt="Aurumward emblem" className="h-9 w-9 rounded-md object-cover" />
            <span className="font-display text-lg font-bold text-ff-gold">Aurumward</span>
          </button>
          <button onClick={() => nav(user ? "/dashboard" : "/login")} data-testid="lb-enter" className="rounded-sm border border-ff-gold/50 px-4 py-1.5 text-sm text-ff-gold hover:bg-ff-gold/10">
            {user ? "Dashboard" : "Sign In"}
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6" data-testid="leaderboards-page">
        <div className="mb-1 flex items-center gap-2">
          <Trophy size={26} weight="duotone" className="text-ff-gold" />
          <h1 className="font-display text-4xl font-bold tracking-tight">Explorer Hall of Fame</h1>
        </div>
        <div className="mb-5 flex flex-wrap items-center gap-3 text-sm text-ff-muted">
          {data?.season && <span className="rounded-sm border border-ff-brass/30 px-2 py-0.5 text-ff-gold">{data.season.name}</span>}
          {data?.season && <span>{countdown(data.season.ends)}</span>}
          <span className="font-mono-ff text-xs">Score v{data?.score_version}</span>
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} data-testid={`lb-cat-${c.id}`}
              className={`rounded-sm border px-3 py-1 text-xs transition-colors ${cat === c.id ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/30 text-ff-muted hover:text-ff-parchment"}`}>
              {c.label}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="ff-panel rounded-md p-10 text-center text-ff-muted" data-testid="lb-empty">
            No ranked explorers yet on this board. Complete expeditions to etch your name.
          </div>
        ) : (
          <>
            <div className="mb-5 grid grid-cols-3 gap-3">
              {podium.map((p, i) => (
                <motion.button key={p.user_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  onClick={() => nav(`/explorer/${p.user_id}`)} data-testid={`podium-${i}`}
                  className={`ff-panel rounded-md p-4 text-center ${i === 0 ? "order-2 border-ff-gold ff-glow-gold" : i === 1 ? "order-1 mt-4" : "order-3 mt-4"}`}>
                  {i === 0 ? <Crown size={28} weight="duotone" className="mx-auto text-ff-gold" /> : <Medal size={24} weight="duotone" className="mx-auto text-ff-brass" />}
                  <div className="mt-1 font-mono-ff text-xs text-ff-muted">#{p.position}</div>
                  <div className="truncate font-display text-lg font-semibold text-ff-parchment">{p.username}</div>
                  <div className="text-xs text-ff-brass">{p.rank}</div>
                  <div className="mt-1 font-mono-ff text-xl font-bold text-ff-gold">{p.value}</div>
                </motion.button>
              ))}
            </div>

            <div className="ff-panel overflow-hidden rounded-md" data-testid="lb-table">
              {rest.map((r) => (
                <button key={r.user_id} onClick={() => nav(`/explorer/${r.user_id}`)} data-testid={`lb-row-${r.position}`}
                  className="flex w-full items-center justify-between border-b border-ff-brass/10 px-4 py-3 text-left transition-colors hover:bg-ff-bg2/60">
                  <div className="flex items-center gap-3">
                    <span className="w-8 font-mono-ff text-sm text-ff-muted">#{r.position}</span>
                    <div>
                      <div className="text-sm font-medium text-ff-parchment">{r.username}</div>
                      <div className="text-xs text-ff-brass">{r.rank} · Rep {r.reputation}</div>
                    </div>
                  </div>
                  <span className="font-mono-ff text-sm font-bold text-ff-gold">{r.value}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {me?.position && (
          <div className="ff-panel mt-4 flex items-center justify-between rounded-md border border-ff-gold/40 p-4" data-testid="lb-my-position">
            <span className="text-sm text-ff-muted">Your position</span>
            <span className="font-mono-ff text-ff-gold">#{me.position} of {me.total} · {me.row?.value}</span>
          </div>
        )}

        <div className="mt-6 flex items-start gap-2 rounded-sm border border-ff-brass/30 bg-ff-bg2/60 px-4 py-3 text-xs text-ff-muted">
          <Info size={16} className="mt-0.5 shrink-0 text-ff-brass" />
          <span>
            Frontier Score is server-calculated from expedition performance, mastery, reputation, skill and responsible risk — never from Gold balance.
            Demo, admin and test accounts are excluded. Ties break by mastery, then reputation, then difficulty, then crew survival, then earliest achieved. Play fair — all scores derive from validated events.
          </span>
        </div>
      </div>
    </div>
  );
}
